import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, X } from "lucide-react";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState({});

  const load = () => http.get("/admin/bookings").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    if (!confirm("Confirm that Layla has received this e-Transfer? This will notify the client.")) return;
    setLoading((p) => ({ ...p, [id]: true }));
    try {
      await http.post(`/admin/bookings/${id}/mark-paid`);
      toast.success("Booking confirmed. Client notified.");
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading((p) => ({ ...p, [id]: false })); }
  };

  const cancelBooking = async (id) => {
    if (!confirm("Cancel this booking?")) return;
    try {
      await http.post(`/admin/bookings/${id}/cancel`);
      toast.success("Booking cancelled.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-bookings-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Calendar</div>
        <h1 className="font-display text-4xl text-champagne">Your <span className="italic text-pink-hot">bookings</span></h1>
        <p className="text-sm text-champagne/60 mt-3 max-w-2xl">
          When a client reserves a slot, they'll send you an e-Transfer. Once it lands in your inbox, click
          <strong className="text-pink-hot"> Mark as Received</strong> to confirm their booking — they'll get a beautiful confirmation email from you automatically.
        </p>
      </div>
      {bookings.length === 0 ? (
        <div className="text-sm text-champagne/60 glass-panel p-8 rounded-md">No bookings yet.</div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => {
            const isPaid = b.payment_status === "paid";
            const isCancelled = b.status === "cancelled";
            return (
              <div
                key={b.id}
                className={`glass-panel rounded-md p-5 flex justify-between items-center gap-4 flex-wrap ${isCancelled ? "opacity-50" : ""}`}
                data-testid={`booking-${b.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display italic text-xl text-champagne">{b.rate_title}</div>
                  <div className="text-xs text-champagne/60 mt-1">{b.date} · {b.time}</div>
                  <div className="text-sm text-champagne/80 mt-1">{b.name} · {b.email} · {b.phone}</div>
                  {b.notes && <div className="text-xs text-rosegold/80 mt-2 italic">Notes: {b.notes}</div>}
                </div>
                <div className="text-right">
                  <span className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-sm ${
                    isCancelled ? "bg-champagne/10 text-champagne/60" :
                    isPaid ? "bg-pink-hot/20 text-pink-hot" : "bg-rosegold/20 text-rosegold"
                  }`}>
                    {isCancelled ? "Cancelled" : isPaid ? "Confirmed" : "Awaiting e-Transfer"}
                  </span>
                  <div className="font-display text-xl text-pink-hot mt-2">${b.deposit_amount}</div>
                  <div className="text-xs text-champagne/60">of ${b.total_amount}</div>
                </div>
                <div className="flex flex-col gap-2 min-w-[180px]">
                  {!isPaid && !isCancelled && (
                    <button
                      onClick={() => markPaid(b.id)}
                      disabled={loading[b.id]}
                      className="px-4 py-2 bg-pink-hot text-white text-[10px] tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all disabled:opacity-60 flex items-center gap-2 justify-center"
                      data-testid={`mark-paid-${b.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {loading[b.id] ? "..." : "Mark as Received"}
                    </button>
                  )}
                  {!isCancelled && (
                    <button
                      onClick={() => cancelBooking(b.id)}
                      className="px-4 py-2 border border-rosegold/30 text-champagne/70 text-[10px] tracking-[0.28em] uppercase rounded-sm hover:border-rosegold/60 transition-all flex items-center gap-2 justify-center"
                      data-testid={`cancel-booking-${b.id}`}
                    >
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
